import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

interface ExportPdfOptions {
  filename: string
  padding?: number
  scale?: number
  ignoreSelectors?: string[]
}

const exportPdf = async (
  element: HTMLElement,
  options: ExportPdfOptions
): Promise<void> => {
  const {
    filename,
    padding = 30,
    scale = 2,
    ignoreSelectors = ['.export-button', '.primary-button'],
  } = options

  const reportHeight = element.scrollHeight
  const reportTop = element.getBoundingClientRect().top + window.scrollY

  const chartElements = Array.from(
    element.querySelectorAll<HTMLElement>('.chart-container')
  )
  const chartRanges = chartElements
    .map(el => {
      const rect = el.getBoundingClientRect()
      const top = rect.top + window.scrollY - reportTop
      const bottom = top + rect.height

      return {
        top: Math.max(top, 0),
        bottom: Math.min(bottom, reportHeight),
      }
    })
    .filter(range => range.bottom > range.top)

  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
    ignoreElements: el =>
      ignoreSelectors.some(
        sel => el.classList?.contains(sel.replace('.', '')) ?? false
      ),
  })

  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const contentWidth = pageWidth - padding * 2
  const contentHeight = pageHeight - padding * 2
  const scaleToPdf = contentWidth / canvas.width
  const pageHeightCanvas = contentHeight / scaleToPdf
  const chartTopGapPdf = 30
  const chartTopGapCanvas = chartTopGapPdf / scaleToPdf

  const chartRangesCanvas = chartRanges
    .map(range => ({
      top: range.top * (canvas.height / reportHeight),
      bottom: range.bottom * (canvas.height / reportHeight),
    }))
    .sort((a, b) => a.top - b.top)

  const pageBreaks: number[] = []
  let current = 0
  while (current < canvas.height - 1) {
    let nextBreak = current + pageHeightCanvas
    if (nextBreak >= canvas.height) {
      pageBreaks.push(canvas.height)

      break
    }
    const blockingChart = chartRangesCanvas.find(
      range => range.top < nextBreak && range.bottom > nextBreak
    )
    if (blockingChart && blockingChart.top > current + 1) {
      nextBreak = blockingChart.top
    }
    const nearBottomChart = chartRangesCanvas.find(
      // eslint-disable-next-line @typescript-eslint/no-loop-func
      range =>
        range.top > current + 1 &&
        range.top < nextBreak &&
        nextBreak - range.top < chartTopGapCanvas
    )
    if (nearBottomChart) {
      nextBreak = nearBottomChart.top
    }
    if (nextBreak <= current + 1) {
      nextBreak = Math.min(current + pageHeightCanvas, canvas.height)
    }
    pageBreaks.push(nextBreak)
    current = nextBreak
  }

  let pageStart = 0
  pageBreaks.forEach((breakPoint, index) => {
    const sliceHeight = Math.max(breakPoint - pageStart, 1)
    const sliceCanvas = document.createElement('canvas')
    sliceCanvas.width = canvas.width
    sliceCanvas.height = sliceHeight
    const sliceContext = sliceCanvas.getContext('2d')
    if (sliceContext) {
      sliceContext.drawImage(
        canvas,
        0,
        pageStart,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight
      )
    }

    const sliceData = sliceCanvas.toDataURL('image/png')
    const sliceHeightPdf = sliceHeight * scaleToPdf
    if (index > 0) {
      pdf.addPage()
    }
    pdf.addImage(
      sliceData,
      'PNG',
      padding,
      padding,
      contentWidth,
      sliceHeightPdf
    )
    pageStart = breakPoint
  })

  pdf.save(filename)
}

export default exportPdf
